<template>
  <div class="edit-org-page py-3">
    <header class="edit-org-toolbar">
      <button type="button" class="back-btn" @click="goBack" aria-label="Back to organisations">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="19" y1="12" x2="5" y2="12"/>
          <polyline points="12 19 5 12 12 5"/>
        </svg>
        <span>Organisations</span>
      </button>
      <div class="edit-org-toolbar__title">
        <span class="edit-org-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Edit Organisation</h1>
          <p>Update the organisation name or replace its logo</p>
        </div>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="edit-org-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="edit-org-alert">{{ successMessage }}</CAlert>

    <section class="edit-org-card">
      <header class="edit-org-card__head">
        <h2>Organisation details</h2>
        <p v-if="form.name">Editing <strong>{{ form.name }}</strong> · Id {{ orgId }}</p>
        <p v-else>Loading…</p>
      </header>

      <form class="edit-org-form" @submit.prevent="submit">
        <div v-if="isLoading" class="edit-org-loading">
          <CSpinner size="sm" />
          <span>Loading organisation…</span>
        </div>

        <template v-else>
          <div class="form-grid">
            <div class="form-field form-field--name">
              <label for="org-name" class="form-label">
                Name
                <span class="form-label__required" aria-hidden="true">*</span>
              </label>
              <input
                id="org-name"
                v-model.trim="form.name"
                type="text"
                class="form-input"
                placeholder="e.g. Manakkal Agro Pharma"
                autocomplete="off"
                :disabled="isSubmitting"
                :class="{ 'has-error': fieldErrors.name }"
                @input="clearFieldError('name')"
              />
              <span v-if="fieldErrors.name" class="form-hint form-hint--error">{{ fieldErrors.name }}</span>
              <span v-else class="form-hint">Use the full legal or trading name — it must be unique.</span>
            </div>

            <div class="form-field form-field--logo">
              <label class="form-label">Logo</label>
              <OrganisationLogoUploader
                v-model="form.logoUrl"
                :disabled="isSubmitting"
                :alt="form.name || 'Organisation logo'"
              />
              <span class="form-hint">Upload a square PNG, JPG, WebP or SVG. The URL is saved to the organisation record.</span>
            </div>
          </div>

          <div class="form-actions">
            <button type="button" class="cancel-btn" :disabled="isSubmitting" @click="goBack">Cancel</button>
            <button type="submit" class="submit-btn" :disabled="isSubmitting || !canSubmit">
              <CSpinner v-if="isSubmitting" size="sm" class="submit-btn__spinner" />
              <span>{{ isSubmitting ? 'Saving…' : 'Save Changes' }}</span>
            </button>
          </div>
        </template>
      </form>
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import OrganisationLogoUploader from './OrganisationLogoUploader.vue'

export default {
  name: 'SettingsEditOrganisation',
  components: { OrganisationLogoUploader },
  data () {
    return {
      isLoading: true,
      isSubmitting: false,
      form: {
        name: '',
        logoUrl: ''
      },
      original: {
        name: '',
        logoUrl: ''
      },
      fieldErrors: {},
      errorMessage: '',
      successMessage: ''
    }
  },
  computed: {
    orgId () {
      return this.$route.params.id
    },
    canSubmit () {
      const name = this.form.name.trim()
      if (!name) return false
      const nameChanged = name !== this.original.name
      const logoChanged = (this.form.logoUrl || '') !== (this.original.logoUrl || '')
      return nameChanged || logoChanged
    }
  },
  created () {
    this.loadData()
  },
  methods: {
    loadData () {
      this.isLoading = true
      this.errorMessage = ''
      const token = localStorage.getItem('api_token')

      axios.get(`${this.$apiAdress}/api/organisations/${this.orgId}?token=${token}`)
        .then((response) => {
          const org = response.data?.data || response.data
          if (!org || !org.id) {
            this.errorMessage = 'Organisation not found.'
            return
          }
          this.form.name = org.name || ''
          this.form.logoUrl = org.logo_url || ''
          this.original.name = this.form.name
          this.original.logoUrl = this.form.logoUrl
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to load this organisation.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    clearFieldError (field) {
      if (this.fieldErrors[field]) {
        this.$delete(this.fieldErrors, field)
      }
      this.errorMessage = ''
    },
    validate () {
      const errors = {}
      const name = this.form.name.trim()
      if (!name) {
        errors.name = 'Organisation name is required.'
      } else if (name.length > 255) {
        errors.name = 'Name must be 255 characters or fewer.'
      }
      this.fieldErrors = errors
      return Object.keys(errors).length === 0
    },
    submit () {
      if (!this.validate()) return

      this.isSubmitting = true
      this.errorMessage = ''
      this.successMessage = ''

      const token = localStorage.getItem('api_token')
      const payload = {
        name: this.form.name.trim(),
        logo_url: this.form.logoUrl ? this.form.logoUrl.trim() : null
      }

      axios.put(`${this.$apiAdress}/api/organisations/${this.orgId}?token=${token}`, payload)
        .then((response) => {
          const validationErrors = response.data?.error
          if (validationErrors && typeof validationErrors === 'object') {
            this.fieldErrors = this.flattenValidationErrors(validationErrors)
            this.errorMessage = 'Please fix the errors below.'
            return
          }

          this.successMessage = response.data?.message || 'Organisation updated successfully.'
          this.original.name = this.form.name.trim()
          this.original.logoUrl = this.form.logoUrl
          setTimeout(() => {
            this.$router.push('/settings/organisations')
          }, 600)
        })
        .catch((error) => {
          console.log(error)
          const data = error?.response?.data
          if (data?.error && typeof data.error === 'object') {
            this.fieldErrors = this.flattenValidationErrors(data.error)
          }
          this.errorMessage = data?.message
            || 'Unable to update organisation. You may not have permission.'
        })
        .finally(() => {
          this.isSubmitting = false
        })
    },
    flattenValidationErrors (errors) {
      const flat = {}
      Object.keys(errors).forEach((key) => {
        const value = errors[key]
        flat[key] = Array.isArray(value) ? value[0] : String(value)
      })
      return flat
    },
    goBack () {
      this.$router.push('/settings/organisations')
    }
  }
}
</script>

<style scoped>
.edit-org-page {
  padding: 0.25rem 0;
  max-width: 880px;
}

.edit-org-toolbar {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.25rem 0.25rem 1rem;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.55rem;
  border: 0;
  background: transparent;
  color: #6b7280;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.15s ease, color 0.15s ease;
}

.back-btn:hover {
  background: #f5fbf8;
  color: #0e5b46;
}

.edit-org-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.edit-org-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.edit-org-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.edit-org-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.edit-org-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

.edit-org-card {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.edit-org-card__head {
  padding: 0.85rem 1.25rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.edit-org-card__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.edit-org-card__head p {
  margin: 0.2rem 0 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.edit-org-card__head strong {
  color: #1f2a44;
}

.edit-org-form {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.edit-org-loading {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #4b5563;
  font-weight: 600;
  padding: 1rem 0;
}

.form-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
  gap: 1.25rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.form-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.form-label__required {
  color: #b45309;
  margin-left: 0.15rem;
}

.form-input {
  width: 100%;
  height: 38px;
  padding: 0.3rem 0.85rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.85rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.form-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.form-input:disabled {
  background: #f8fafc;
  cursor: not-allowed;
}

.form-input.has-error {
  border-color: #dc2626;
}

.form-input.has-error:focus {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.15);
}

.form-hint {
  font-size: 0.74rem;
  color: #6b7280;
}

.form-hint--error {
  color: #b91c1c;
  font-weight: 600;
}

.form-hint--warn {
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  padding: 0.4rem 0.55rem;
  border-radius: 6px;
  line-height: 1.35;
}

.form-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.6rem;
  padding-top: 0.5rem;
  border-top: 1px solid #eef1f7;
  margin-top: 0.5rem;
}

.cancel-btn {
  height: 38px;
  padding: 0 1.1rem;
  border-radius: 8px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.cancel-btn:hover:not(:disabled) {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.cancel-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.submit-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 38px;
  padding: 0 1.25rem;
  border-radius: 8px;
  border: 0;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
  filter: brightness(0.96);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  box-shadow: none;
}

.submit-btn__spinner {
  width: 14px;
  height: 14px;
}

@media (max-width: 767px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 575px) {
  .edit-org-page {
    padding: 0 0 0.5rem;
  }
  .edit-org-toolbar {
    flex-wrap: wrap;
    padding: 0.25rem 0 0.75rem;
    gap: 0.5rem;
  }
  .edit-org-toolbar__accent {
    height: 30px;
  }
  .edit-org-toolbar__title h1 {
    font-size: 1rem;
  }
  .edit-org-toolbar__title p {
    font-size: 0.72rem;
  }
  .edit-org-card__head {
    padding: 0.75rem 0.85rem;
  }
  .edit-org-form {
    padding: 0.85rem;
  }
  .form-actions {
    flex-direction: column-reverse;
    gap: 0.5rem;
  }
  .cancel-btn,
  .submit-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
